/**
 * THE PTA/PTB STRUCTURE, DRAWN: vertical time bands and the horizontal rays they cast.
 *
 * <pre>
 *   ░░░░░░                                    ░░░░░░
 *   ░band░  ──────── TOP LINE ────────────┐   ░band░   (lines reset here)
 *   ░ N  ░  ──────── BOTTOM LINE ─────────┘   ░ N+1░
 *   ░░░░░░                                    ░░░░░░
 *           valid from the day AFTER band N
 *           to the day BEFORE band N+1 opens
 * </pre>
 *
 * <h2>Everything here is DRAWN, not derived</h2>
 *
 * The levels and the validity windows arrive from the backend, computed by the same
 * `TimeBandLines` table the eight entry rules were evaluated against. This module does no
 * arithmetic on prices at all — it maps dates onto the axis and emits ECharts geometry.
 *
 * <p>That is the whole point of the feature. A visual audit asks "did the FSM put the line
 * THERE", and a chart that recomputed the extremes in the browser would be answering a different
 * question — it would agree with itself whatever the engine had done. The CTS overlay recomputes
 * its own fold for a good reason (the chart reads that dataset straight from the bucket and never
 * touches the backend); these lines have no such excuse, because the run that produced them is
 * the thing being audited.
 *
 * <h2>Bands are shown in full; LINES only once their band has closed</h2>
 *
 * A band is a calendar interval and carries no price information, so drawing all of them — past
 * and future — reveals nothing the engine did not already know from the start.
 *
 * <p>A LINE is different: its level is an extreme of the bars inside its band, so it exists only
 * after that band has closed. Showing one before the playhead reaches `validFrom` would put a
 * future high on screen. Once it does exist, the ray is drawn across its WHOLE validity window
 * even if that runs past the playhead, because the window's end is the next band's start — again
 * pure calendar, no price.
 */

/*
 * THE PENS COME FROM `simulatorPalette`, not from literals here.
 *
 * They were `#4fc3f7` (blue-grey) and `#ce93d8` (violet), chosen before there was a palette. PTA
 * is now amber and PTB sky blue, which also frees violet for CTS — under the old pair, PTB violet
 * and the CTS pillars were two shades of the same hue on one chart.
 *
 * Re-exported under their old names so every existing importer and every existing test keeps
 * working: what changed is the value, which is exactly the kind of change that must happen in one
 * place. See the palette's own note on PTA amber sitting close to the playhead's.
 */
// IMPORTED as well as re-exported. `export … from` re-exports without binding the name locally,
// so `colourFor` below would have been reading an undefined identifier — and the symptom is a
// band drawn with `color: undefined`, which ECharts renders in its own default rather than
// erroring.
import { CHANNEL_BORDER_WIDTH, PROJECTION_BORDER_WIDTH, CHANNEL_FILL_BOOST }
  from './bandWeights';
import { guideLevels, GUIDE_LINE_WIDTH } from './bandGuides';

export { PTA_COLOR, PTB_COLOR } from './simulatorPalette';
import { PTA_COLOR, PTB_COLOR } from './simulatorPalette';

/** How the band shading reads: present, but never competing with the candles. */
/**
 * The generation stripe's fill.
 *
 * <p>0.30, up from 0.1, and it moved because {@link CYCLE_OPACITY} did: the stripe marks the
 * interval a band is FORMING in and sits INSIDE the cycle tint, so it has to be the denser of the
 * two or the pair says the opposite of what it means. The ratio is what carries the meaning, not
 * either number.
 */
export const BAND_OPACITY = 0.3;

/**
 * The equator ray's width — the 1.5 all three rays shared before the Top and Bottom took the
 * channel's own weight.
 *
 * <p>Thinner than the edges because it is a midline rather than a bound of the measured range,
 * and because three heavy labelled rays per scope, over 195 scopes, is a wall rather than a
 * structure. Thicker than a derived GUIDE, because the engine ships this one.
 */
export const EQUATOR_LINE_WIDTH = 1.5;

export function colourFor(category) {
  return category === 'PTB' ? PTB_COLOR : PTA_COLOR;
}

/**
 * The index of {@code date} on the axis, or the nearest bar at or after it.
 *
 * <p>A band's start or end is a CALENDAR date and frequently not a trading day — a band ending on
 * a Saturday has no bar of its own. Snapping to the next available bar keeps the shading aligned
 * with what is actually drawn instead of dropping the band entirely, which is what an exact-match
 * lookup would do for roughly two bands in seven.
 *
 * @returns an index, or -1 when the date is past the end of the axis
 */
export function indexAtOrAfter(dates, date) {
  if (!Array.isArray(dates) || dates.length === 0 || !date) return -1;
  // The axis is ascending ISO dates, so a string compare is a date compare.
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] >= date) {
      found = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return found;
}

/** The nearest bar at or BEFORE {@code date}, for a band's closing edge. */
export function indexAtOrBefore(dates, date) {
  if (!Array.isArray(dates) || dates.length === 0 || !date) return -1;
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] <= date) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * Shaded vertical regions, one per band.
 *
 * <p>An ECharts `markArea` pair per band. The x coordinates are axis CATEGORIES (dates), and the
 * y bounds are omitted so the band spans the grid's full height at any zoom — the same reason the
 * playhead is a `markLine` rather than a `lines` series.
 */
export function bandAreas(timeBands, dates) {
  if (!Array.isArray(timeBands) || !Array.isArray(dates) || dates.length === 0) return [];
  const out = [];
  timeBands.forEach((band) => {
    if (!band) return;
    const from = indexAtOrAfter(dates, band.bandStart);
    const to = indexAtOrBefore(dates, band.bandEnd);
    // Entirely outside the chart, or a band whose every day was a holiday.
    if (from < 0 || to < 0 || to < from) return;
    const colour = colourFor(band.category);
    out.push([
      {
        xAxis: dates[from],
        // NO OUTLINE, for the reason the other two tiers state: a markArea on a candlestick
        // inherits that series' border unless it is told not to.
        itemStyle: { color: colour, opacity: BAND_OPACITY,
          borderWidth: PROJECTION_BORDER_WIDTH },
        // The category, once per band, so two overlapping timelines stay readable.
        label: {
          show: true,
          formatter: band.category,
          position: 'insideTop',
          color: colour,
          fontSize: 10,
          fontWeight: 'bold',
        },
      },
      { xAxis: dates[to] },
    ]);
  });
  return out;
}

/**
 * The CYCLE tint: fainter than the generation stripe, because it covers twenty times as much.
 *
 * <h2>0.14, up from 0.035 — and the canvas is why</h2>
 *
 * 0.035 was chosen against the GENERATION STRIPE's 0.1: a PTA cycle is 225 days and its interval
 * 20, so matching the stripe would have made the tint a wash and the stripe inside it invisible
 * as a stripe. That reasoning is about the two marks and it ignored the thing they are both drawn
 * on, which is a WHITE chart. Composited over white, 0.035 of amber is #fffcf6 — three values of
 * blue away from the page, which is below what a reader can see as a REGION at all rather than
 * merely faint.
 *
 * <pre>
 *   opacity   #F59E0B over white   visible as a channel?
 *   0.035           #fffcf6        no -- 3 values in the blue channel, 0 in red
 *   0.08            #fef7eb        barely
 *   0.14 (shipped)  #fef1dd        yes, and the candles read through it cleanly
 *   0.25            #fce7c2        yes, and it starts competing with the candles
 * </pre>
 *
 * <p>Those four are computed from {@code PTA_COLOR} rather than eyeballed, and a test recomputes
 * the shipped row so the table cannot drift from the constant beside it.
 *
 * <h2>And it SURVIVED the dark theme, which was not a foregone conclusion</h2>
 *
 * The table above is a measurement against white, so the arrival of a charcoal canvas put it in
 * the same position as the handle's ink core — which inverted outright. Checked rather than
 * assumed: 0.14 of amber over `#0d1117` composites to `#2d2515`, which is **1.250** against the
 * canvas where the light case is **1.110**. Alpha compositing lifts more per unit of alpha in the
 * shadows, so the tint reads as a region at least as well on dark and the opacity is unchanged.
 *
 * <p>Recorded because "it still works" is the kind of claim that is only worth anything if
 * somebody actually computed it; `themeCanvas.test.js` does, for both canvases.
 *
 * <p>The stripe moved with it, for the reason the two opacities were ever related: at 0.14 against
 * the old 0.1 the tint would have been DARKER than the interval inside it, which inverts what the
 * pair says. {@link BAND_OPACITY} is 0.30 now, so the interval still reads as the denser mark.
 */
export const CYCLE_OPACITY = 0.14;

/**
 * THE ACTIVE STRUCTURAL WINDOW, drawn continuously from one pillar's start to the next one's.
 *
 * <h2>What this makes visible, and what it deliberately does not</h2>
 *
 * The generation stripe marks the interval a band is FORMING in; the rays mark the stretch its
 * levels are in force over. Between them they cover the whole calendar, but as two different
 * marks with a visible seam — so the thing a reader cannot currently see at a glance is the
 * CYCLE: this band, from the moment it starts generating to the moment the next one does.
 *
 * <p>So this is one continuous tint per cycle, `bandStart` to the NEXT band's `bandStart` of the
 * same category, with the darker generation stripe sitting inside it.
 *
 * <h2>Why the LINES are not extended to match, which is what was asked</h2>
 *
 * Because the engine has no lines there. `TimeBandLines` gives a scope `validFrom = band.end + 1`
 * to `validTo = next band.start - 1`, so during a generation interval no level is in force for
 * the FSM either — which is why the rays stop, and it is correct that they do. Measured on the
 * shipped datasets: PTB's lines are absent for 11 days of every 88, PTA's for 20 of every 225.
 * That is the "disappearing blue line".
 *
 * <p>Drawing a level across the gap would put a price on screen that the engine is not trading
 * against, which is the one thing this overlay exists not to do. The tint closes the gap for the
 * eye without inventing a level: the cycle reads as continuous, and the stripe inside it says
 * why the lines pause.
 *
 * <h2>BOUNDED BY THE BAND'S OWN CHANNEL, not by the whole vertical axis</h2>
 *
 * It shipped as an `xAxis`-only pair, which in ECharts means full height — so the tint washed the
 * entire chart background from the volume pane to the top of the price grid. At 0.035 that is
 * faint, and it is still the wrong claim: what the cycle highlights is the structural WINDOW,
 * which has a top and a bottom.
 *
 * <p>So each tint is a `coord` pair bounded by its band's own `top` and `bottom` — the same two
 * numbers the Top and Bottom rays are drawn at. The shaded region is exactly the channel those
 * rays bound, extended across the cycle.
 *
 * <p>A band missing either level is SKIPPED rather than falling back to full height: a channel
 * with one edge is not a channel, and a tint that silently became a full-height wash for some
 * bands and not others would be the least legible outcome available.
 *
 * <h2>Still calendar in its X extent, and still forward</h2>
 *
 * The two dates are from an ephemeris, so the tint is drawn across the whole axis at any playhead
 * like the stripes and the CTS pillars. Its Y bounds are prices — but they are the band's OWN
 * extremes, which is the same information the rays carry and are subject to the same rule: they
 * are only knowable once the band has closed.
 *
 * <p>THAT IS WHY THE TINT TAKES A PLAYHEAD. A cycle whose band has not closed yet is drawn with
 * no tint at all rather than with a guessed channel — the levels do not exist, and the generation
 * stripe already marks the interval.
 */
/**
 * The last column a HORIZONTAL mark may reach, or the end of the axis when nothing is masked.
 *
 * <h2>⚠ A LEVEL MAY NOT BE DRAWN OVER A BAR THAT HAS NO PRICE</h2>
 *
 * The rule this file already states — a band may be shown ahead of the playhead and a LINE may
 * not — was applied only to whether a ray appears AT ALL. Once a band had closed its ray ran to
 * the end of its validity window, which on a replay is deep into masked space, so a Top line sat
 * across thirty columns of empty canvas looking like a statement about bars nobody has seen.
 *
 * <p>The level itself is knowable there, which is why this reads as harmless and is not: the
 * reader cannot tell a line drawn over revealed price from one drawn over nothing, so the same
 * mark means two different things along its own length. The VERTICALS are the exception and keep
 * running — a generation stripe and a band edge are calendar, carry no price, and standing in
 * empty space is the whole point of them.
 *
 * @param dates the axis
 * @param upToDate the playhead, or null/undefined when nothing is masked
 */
export function lastDrawableIndex(dates, upToDate) {
  if (!Array.isArray(dates) || dates.length === 0) return -1;
  if (!upToDate) return dates.length - 1;
  // AT OR BEFORE, so a playhead on a non-trading day clips to the last bar that exists rather
  // than to nothing.
  return indexAtOrBefore(dates, upToDate);
}

export function cycleSpans(timeBands, dates, upToDate) {
  if (!Array.isArray(timeBands) || !Array.isArray(dates) || dates.length === 0) return [];
  const lastDate = dates[dates.length - 1];
  const out = [];

  // BY CATEGORY: "the next pillar" means the next one of the SAME system. Interleaving PTA and
  // PTB would make each cycle end at whichever band happened to come next on the calendar.
  const byCategory = new Map();
  timeBands.forEach((band) => {
    if (!band || !band.bandStart) return;
    if (!byCategory.has(band.category)) byCategory.set(band.category, []);
    byCategory.get(band.category).push(band);
  });

  // The right-hand limit for every rectangle below. The tint and the projections are HORIZONTAL
  // marks -- they are bounded in price -- so neither may run past the last revealed bar.
  const limit = lastDrawableIndex(dates, upToDate);
  if (limit < 0) return out;

  byCategory.forEach((bands, category) => {
    const ordered = [...bands].sort((a, b) => (a.bandStart < b.bandStart ? -1 : 1));
    ordered.forEach((band, i) => {
      const from = indexAtOrAfter(dates, band.bandStart);
      if (from < 0) return;
      // To the next band's START, exclusive: the next cycle's tint begins on that column, and
      // two tints sharing it would double the opacity on one bar of every cycle.
      const next = ordered[i + 1];
      const endDate = next ? next.bandStart : lastDate;
      const to = next
        ? Math.max(from, indexAtOrBefore(dates, endDate) - 1)
        : indexAtOrBefore(dates, endDate);
      if (to < 0 || to < from) return;
      const clipped = Math.min(to, limit);
      // Nothing revealed inside this cycle yet -- so there is no bar for the channel to be drawn
      // over, and a rectangle here would be the defect this clip exists to prevent.
      if (clipped < from) return;
      // A CHANNEL, or nothing. A band with one edge is not a channel, and a band whose levels
      // are not knowable yet at this playhead has none at all -- the same rule the rays follow.
      if (!Number.isFinite(band.top) || !Number.isFinite(band.bottom)) return;
      if (!band.validFrom || (upToDate && band.validFrom > upToDate)) return;
      out.push({ category, band, from, to: clipped, colour: colourFor(category) });
    });
  });
  return out;
}

/**
 * THE CHANNEL TINT, one rectangle per cycle.
 *
 * <p>Built from {@link cycleSpans}, which is what makes the projections above and below it share
 * its horizontal boundaries by CONSTRUCTION rather than by two copies of the same arithmetic
 * agreeing. They were two copies, and a reader could not tell by looking whether a seam between
 * a channel and its projection was a rendering artefact or a real disagreement about where a
 * cycle ends.
 */
export function cycleAreas(timeBands, dates, upToDate) {
  return cycleSpans(timeBands, dates, upToDate).map(({ band, from, to, colour }) => ([
    {
      coord: [dates[from], band.top],
      /*
       * THE MEASURED BAND, said by its FILL alone now.
       *
       * <p>It carried a border, and a markArea takes one border for all four sides -- so the
       * channel came with a heavy vertical cap at each end of its validity window. Neither cap
       * is a reading: the generation stripe already marks where the band formed, and the ray
       * ends where the next scope begins. The horizontal edges are drawn by `lineRays`, which
       * is where they belong anyway: the Top and Bottom are LEVELS, and a level is a line.
       *
       * <p>What still distinguishes the measured band from its projections is the fill, a touch
       * richer than theirs, and those two labelled rays.
       */
      itemStyle: {
        color: colour,
        opacity: CHANNEL_OPACITY,
        borderWidth: PROJECTION_BORDER_WIDTH,
      },
    },
    { coord: [dates[to], band.bottom] },
  ]));
}

/**
 * THE EXTENDED RANGE, at the CHANNEL'S OWN WEIGHT.
 *
 * <p>It shipped at 0.055 — two and a half times fainter than the channel, so that a reader could
 * never mistake PROJECTED range for measured range. That was overruled on sight of the intended
 * design: the three tiers are meant to read as one cohesive structure of companion channels, and
 * at 0.055 over a light canvas the projections composite to within a few values of the page and
 * barely register as regions at all.
 *
 * <p>So it is {@link CYCLE_OPACITY} exactly, and declared as that constant rather than as a
 * second 0.14 — the point is that it MATCHES, and two literals agree only until somebody edits
 * one of them.
 *
 * <p>WHAT STILL SEPARATES THEM, now that density does not: the Top and Bottom RAYS are drawn at
 * the channel's own edges — solid for the Top, dashed for the Bottom, each labelled with its
 * level. The measured band is the one between the two labelled lines, which is a stronger
 * discriminator than a tint difference and was already on the chart.
 */
export const EXTENDED_OPACITY = CYCLE_OPACITY;

/**
 * THE MEASURED CHANNEL'S FILL, a touch richer than its projections'.
 *
 * <p>The second half of distinguishing the middle band, after its stroke. Deliberately subtle and
 * derived from {@link EXTENDED_OPACITY} by {@link CHANNEL_FILL_BOOST} rather than typed: the
 * three tiers were asked to read as one cohesive structure, so this LIFTS THE MIDDLE rather than
 * fading the flanks, and the relationship is what has to hold rather than either number.
 *
 * <p>Still under {@link BAND_OPACITY}, which it has to be: the generation stripe sits inside the
 * cycle tint and marks the interval the band is FORMING in, so it stays the denser mark.
 */
export const CHANNEL_OPACITY = Math.round(EXTENDED_OPACITY * CHANNEL_FILL_BOOST * 1000) / 1000;

/**
 * How many band-heights to project above and below. ONE each way, as specified.
 *
 * <p>Named rather than inlined because it is the thing most likely to be asked for differently
 * — two bands either way is one edit here and nothing else.
 */
export const EXTENDED_MULTIPLES = 1;

/**
 * THE PROJECTED RANGE: a band of the SAME HEIGHT directly above and below the channel.
 *
 * <pre>
 *   top + h   ┌──────────────────┐  the upper projection
 *   top       ├──────────────────┤
 *             │   the CHANNEL    │  cycleAreas draws this, denser
 *   bottom    ├──────────────────┤
 *   bottom-h  └──────────────────┘  the lower projection
 *             ^ pillar            ^ the next pillar of the same category
 * </pre>
 *
 * <h2>It spans pillar to pillar, which is why it is built like {@link cycleAreas}</h2>
 *
 * The span asked for is "outward from the originating time pillar to the next pillar", which is
 * exactly the cycle the channel tint already covers — so this groups BY CATEGORY and ends at the
 * next band of the SAME system for the same reason that one does: interleaving PTA and PTB would
 * make each projection end at whichever band happened to come next on the calendar.
 *
 * <p>It also inherits that function's two refusals, and both matter here. A band with only one
 * finite level has no HEIGHT, so there is nothing to project and nothing is drawn rather than a
 * guess. And a band whose levels are not knowable at the playhead is skipped outright — a
 * projection is derived from the extremes, so drawing one early would put a future high on
 * screen just as surely as drawing the ray would.
 *
 * <h2>Each projection is its own datum, not one tall box</h2>
 *
 * A single rectangle from {@code bottom - h} to {@code top + h} would tint the channel a second
 * time, doubling its opacity and destroying the contrast that tells a reader which band is
 * which. Two separate areas leave the middle to {@link cycleAreas}.
 *
 * @returns markArea coord pairs, oldest cycle first, upper projection before lower
 */
export function extendedRangeAreas(timeBands, dates, upToDate) {
  const out = [];
  cycleSpans(timeBands, dates, upToDate).forEach(({ band, from, to, colour }) => {
    const height = band.top - band.bottom;
    // A ZERO-HEIGHT CHANNEL PROJECTS NOTHING. Top equal to bottom is a band whose bars all
    // printed one price; two zero-height rectangles stacked on it would be invisible marks
    // that still cost a datum each.
    if (!(height > 0)) return;
    const reach = height * EXTENDED_MULTIPLES;
    [[band.top + reach, band.top], [band.bottom, band.bottom - reach]].forEach(
      ([upper, lower]) => {
        out.push([
          {
            coord: [dates[from], upper],
            // NO OUTLINE, stated explicitly and shared with the other renderer.
            //
            // A markArea inherits unstated style from the series it is attached to, and this one
            // rides a CANDLESTICK -- whose own itemStyle carries a border. Left to the default
            // the projections drew as thin outlined boxes, which against a fill this faint was
            // all a reader could see of them.
            itemStyle: {
              color: colour,
              opacity: EXTENDED_OPACITY,
              borderWidth: PROJECTION_BORDER_WIDTH,
            },
          },
          { coord: [dates[to], lower] },
        ]);
      },
    );
  });
  return out;
}

/** The band edges: 1px, the band's own colour, faint enough to bound without competing. */
export const EDGE_OPACITY = 0.45;
export const EDGE_WIDTH = 1;

/**
 * THE CRISP EDGES: a faint vertical at each end of every band.
 *
 * <h2>Why a 10%-opacity fill needs them</h2>
 *
 * `BAND_OPACITY` is 0.1, which is right for a background a reader audits price action against and
 * wrong for reading a boundary off: against candles the edge of the stripe is a gradient the eye
 * has to guess at, so "did this bar close inside the generation interval" — the question the
 * whole no-trade / runaway vocabulary turns on — is answered by estimating. A hairline at each
 * end makes the interval a bounded box rather than a smudge, and costs two marks per band.
 *
 * <h2>On the SAME markLine as the rays</h2>
 *
 * A vertical is `{xAxis: date}` and a ray is a `[{coord}, {coord}]` pair; ECharts reads each datum
 * independently, so one markLine carries both. A second series would need its own z-order and
 * could eventually draw underneath the band whose edge it is.
 *
 * <p>A one-day band emits ONE edge, not two on the same column: two identical verticals are a
 * line drawn twice at double the opacity, which is the one place a boundary would look darker
 * than the band it bounds.
 */
export function bandEdges(timeBands, dates) {
  if (!Array.isArray(timeBands) || !Array.isArray(dates) || dates.length === 0) return [];
  const out = [];
  timeBands.forEach((band) => {
    if (!band) return;
    const from = indexAtOrAfter(dates, band.bandStart);
    const to = indexAtOrBefore(dates, band.bandEnd);
    if (from < 0 || to < 0 || to < from) return;
    const colour = colourFor(band.category);
    const edge = (index) => ({
      xAxis: dates[index],
      lineStyle: {
        color: colour, width: EDGE_WIDTH, type: 'solid', opacity: EDGE_OPACITY,
      },
      // The band already carries its category label at the top of the stripe; ECharts labels a
      // markLine by default, so without this every edge repeats it and a narrow band becomes
      // three overlapping words.
      label: { show: false },
    });
    out.push(edge(from));
    if (to !== from) out.push(edge(to));
  });
  return out;
}

/**
 * The Top and Bottom rays, as `markLine` coordinate pairs.
 *
 * <p>Two points per ray, which ECharts draws as a segment: `[{coord:[x1,y]},{coord:[x2,y]}]`. The
 * same shape the trade vectors use, and a `lines` series is deliberately avoided — that one
 * defaults to the GEO coordinate system and on a chart with no geo component it validates, draws
 * nothing, and warns about nothing. That trap has cost this repository a day once already.
 *
 * @param upToDate the playhead. A ray appears only once its band has closed at or before this,
 *                 because its LEVEL is an extreme of that band's bars
 */
/**
 * How old a line is, in CALENDAR days, as a label suffix.
 *
 * <p>Measured from `validFrom` -- the day after the band closed -- because that is the day the
 * line came into existence. Not from the band's start: a band's extremes are not known until it
 * ends, so the level does not exist before then. The same definition
 * `scripts/diagnose-pta-line-age.py` measured with, which is what makes the number on screen the
 * number in the finding.
 *
 * <p>Calendar days rather than bars, deliberately: the chart has a date axis and the reader is
 * comparing against a band's own dates, which are calendar dates. Counting sessions would need the
 * exchange calendar and would not match the published bucket boundaries.
 *
 * <p>Empty string when either date is missing, so a label never reads "(NaNd)".
 */
/**
 * The categories whose EQUILIBRIUM is drawn: both of them.
 *
 * <h2>It used to be PTA alone, and that conflated two different questions</h2>
 *
 * The old note said drawing a midline for PTB "would assert a trigger that run does not have",
 * and the premise is true -- PTB trades the eight micro-crossing rules, which fire on the two
 * outer lines, and it has no midline rule. What does not follow is that the LINE should be
 * absent. A midline is the halfway point between two levels the band already casts; it is
 * structure, and a reader auditing a PTB run wants to see where the middle of the channel is for
 * the same reason they want the channel.
 *
 * <p>Every other mark on this chart is drawn without regard to which rules read it: the
 * generation stripes, the Top and Bottom rays and the CTS pillars are all shown on runs that
 * trade none of them. The midline was the one exception, and it was an exception because the
 * browser derived it and so had to decide for itself when to.
 *
 * <p>It does not any more -- {@code TimeBandLines.Scope.equator()} computes it, the DTO ships it
 * and this draws what it is given -- so the list is now about presentation only. The DOTTED style
 * is what distinguishes it from the solid Top and dashed Bottom, and the `Eq` label names it.
 */
export const EQUATOR_CATEGORIES = ['PTA', 'PTB'];

/**
 * THE NO-TRADE ZONE IS GONE, and its removal is the whole point of the equator architecture.
 *
 * <p>The containment field's first and strongest rule was that a close inside the box produces
 * nothing, and the interior was shaded to say so. The equator sits in the exact middle of that
 * box, so the two cannot both hold — a primary trigger that fires only inside the interior is
 * unreachable under a rule forbidding the interior.
 *
 * <p>Left as a named export rather than deleted silently, because a chart still shading the
 * interior would be telling an auditor the opposite of what the engine now does, and this is the
 * line that says so.
 */
export const INTERIOR_IS_TRADEABLE = true;

export function ageSuffix(validFrom, asOf) {
  if (!validFrom || !asOf) return '';
  const from = Date.parse(`${validFrom}T00:00:00Z`);
  const at = Date.parse(`${String(asOf).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(at)) return '';
  const days = Math.round((at - from) / 86400000);
  return days < 0 ? '' : ` (${days}d)`;
}

export function lineRays(timeBands, dates, upToDate) {
  if (!Array.isArray(timeBands) || !Array.isArray(dates) || dates.length === 0) return [];
  const lastDate = dates[dates.length - 1];
  const limit = lastDrawableIndex(dates, upToDate);
  if (limit < 0) return [];
  const out = [];

  timeBands.forEach((band) => {
    if (!band || !band.validFrom) return;
    // NOT YET KNOWN. The band has not closed as of the playhead, so its extremes do not exist and
    // drawing them would put a future high on screen.
    if (upToDate && band.validFrom > upToDate) return;

    const from = indexAtOrAfter(dates, band.validFrom);
    if (from < 0) return;
    // validTo null means "nothing has replaced these yet", which runs to the end of the series.
    const end = band.validTo == null || band.validTo > lastDate ? lastDate : band.validTo;
    const unclipped = indexAtOrBefore(dates, end);
    if (unclipped < 0 || unclipped < from) return;
    // ...and NEVER past the playhead. A ray is a LEVEL, so every column it crosses is a column
    // it says something about — see `lastDrawableIndex`. The band having closed is what lets the
    // ray exist at all; it is not a licence to draw it over bars that do not.
    const to = Math.min(unclipped, limit);
    if (to < from) return;

    const colour = colourFor(band.category);
    /*
     * THE EQUATOR IS READ FROM THE DTO NOW, not divided here.
     *
     * It was derived in the browser, on the reasoning that one division of two numbers already on
     * the record cannot disagree with the engine's own. That was true and it was not the whole
     * story: the decision of WHICH categories have a midline was then made here as well, in a
     * list of this file's own, and the two places disagreed -- the engine computed a midline for
     * every scope and the chart drew one for PTA.
     *
     * So `TimeBandLines.Scope.equator()` is the one definition, `EquatorField` reads it, the DTO
     * ships it, and this draws it. The fallback to the division is for a timeline fetched before
     * the field existed, which is a cached response rather than a hypothetical.
     */
    const midline = Number.isFinite(band.equator)
      ? band.equator
      : (Number.isFinite(band.top) && Number.isFinite(band.bottom)
        ? (band.top + band.bottom) / 2
        : null);
    const levels = [['top', band.top], ['bottom', band.bottom]];
    if (EQUATOR_CATEGORIES.includes(band.category) && midline !== null) {
      levels.push(['equator', midline]);
    }
    levels.forEach(([which, level]) => {
      if (!Number.isFinite(level)) return;
      const dash = which === 'top' ? 'solid' : which === 'bottom' ? 'dashed' : 'dotted';
      const label = which === 'top' ? 'Top' : which === 'bottom' ? 'Btm' : 'Eq';
      /*
       * THE MEASURED EDGES CARRY THE CHANNEL'S WEIGHT, and the midline does not.
       *
       * This is where that weight lives now. It was the rectangle's border, which a markArea
       * applies to all four sides -- so the channel came with a vertical cap at each end of its
       * validity window. The Top and Bottom are LEVELS, and a level is a line.
       *
       * <p>The equator stays thin: it is a midline rather than an edge of the measured range,
       * and three labelled 3px rays per scope over 195 scopes would be a wall. Same hierarchy
       * the cycle presets draw -- measured heavy, derived thin.
       */
      const width = which === 'equator' ? EQUATOR_LINE_WIDTH : CHANNEL_BORDER_WIDTH;
      out.push([
        {
          coord: [dates[from], level],
          lineStyle: { color: colour, width, type: dash },
          label: {
            show: true,
            // e.g. "PTA Top 452.10 (80d)" -- the category, which line, the level, and HOW OLD the
            // line is at the playhead.
            //
            // The age is there because it was the subject of a measurement. The stale-line
            // hypothesis -- that entering on a stop-out bar is only toxic when the line is an
            // antique -- was tested on the 95 admitted PTA trades and REFUTED backwards: the
            // freshest lines were the catastrophic ones (0-10 days: eight trades, zero winners)
            // and the only above-break-even bucket was 90-150 days. A reader stepping through the
            // chart should be able to see the age that was measured rather than take it on trust.
            //
            // Derived here rather than shipped from the backend: `validFrom` is already on the
            // DTO and the playhead date is already in hand, so computing it in two places would
            // be two definitions of a line's age.
            formatter: `${band.category} ${label} ${level.toFixed(2)}`
              + `${ageSuffix(band.validFrom, upToDate || lastDate)}`,
            position: 'insideStartTop',
            color: colour,
            fontSize: 9,
          },
        },
        { coord: [dates[to], level] },
      ]);
    });

    /*
     * THE EXCURSION BOUNDS, one and two band-heights beyond each projection.
     *
     * <p>From the SAME `guideLevels` the cycle presets read, so a replay and a cycle run on one
     * chart cannot disagree about where the bounds of a channel are. That is not a hypothetical
     * worry here: this structure has two renderers, and the projected range bands and PTB's
     * colour were both built on one of them and reported as missing or wrong from the other.
     *
     * <p>UNLABELLED, unlike the three rays above. Those name a level the engine trades against;
     * these are derived from the channel's own height, and five more labels per scope over 195
     * scopes would bury the ones that mean something. The equator is skipped here because the
     * rays above already draw it from the DTO — the engine's own midline rather than a division
     * done twice.
     */
    guideLevels(band.top, band.bottom)
      .filter(g => g.kind !== 'equator')
      .forEach(({ level, dash }) => {
        out.push([
          {
            coord: [dates[from], level],
            lineStyle: { color: colour, width: GUIDE_LINE_WIDTH, type: dash },
            label: { show: false },
          },
          { coord: [dates[to], level] },
        ]);
      });
  });
  return out;
}

/**
 * Everything, as one ECharts series payload.
 *
 * <p>`markArea` and `markLine` on a dedicated, empty, SILENT series — never on the candlesticks,
 * which already carry two writers and where a third would replace the others. Silent so it never
 * steals a hover from the price tooltip underneath.
 *
 * <p>Both are declared even when empty: an ECharts merge cannot REMOVE a mark by omitting it, so
 * a chart that had bands and then switched to a provider with none would keep the old ones frozen
 * on screen for the rest of the session.
 */
export function timeBandMarks(timeBands, dates, upToDate) {
  return {
    markArea: {
      silent: true,
      animation: false,
      // VERTICAL STRIPES ONLY, now. Each datum is an `xAxis`-only pair -- a full-height band
      // with no price extent.
      //
      // It used to also carry `coord`-pair rectangles shading the box interior as a no-trade
      // zone. Under the equator that shading would be the opposite of the truth: the midline is
      // inside the box and is the PRIMARY trigger, so the interior is where most entries happen.
      //
      // ORDER IS PAINT ORDER, and all three tiers overlap somewhere.
      //
      // THE PROJECTIONS FIRST, so the channel is drawn over them rather than under: they are
      // the faintest mark here and the only one that is a projection rather than a measurement,
      // so anything it shares a pixel with must win.
      //
      // THEN THE CYCLE TINTS, then the generation stripe on top. At 0.14 against 0.30 the
      // stripe has to be last or the interval stops reading as the denser thing inside the
      // window.
      data: [
        ...extendedRangeAreas(timeBands, dates, upToDate),
        ...cycleAreas(timeBands, dates, upToDate),
        ...bandAreas(timeBands, dates),
      ],
    },
    markLine: {
      silent: true,
      symbol: 'none',
      animation: false,
      // The EDGES first, so a Top/Bottom ray is drawn over the boundary it starts from rather
      // than under it — the ray is the measurement and the edge is the frame around it.
      data: [...bandEdges(timeBands, dates), ...lineRays(timeBands, dates, upToDate)],
    },
  };
}

export const TIME_BAND_OVERLAY_ID = 'dynaman-time-bands';
export const TIME_BAND_SERIES_ID = 'dynaman-time-bands-series';

/**
 * The overlay the chart renders from, or null when there is nothing to draw.
 *
 * <p>Null rather than an overlay with empty marks, so the caller REMOVES the previous one instead
 * of leaving an empty decoration behind — the same contract the playhead has.
 */
export function buildTimeBandOverlay(timeBands, dates, upToDate) {
  if (!Array.isArray(timeBands) || timeBands.length === 0) return null;
  if (!Array.isArray(dates) || dates.length === 0) return null;
  return {
    id: TIME_BAND_OVERLAY_ID,
    type: 'timebands',
    label: 'PTA / PTB time bands',
    timeBands,
    upToDate: upToDate || null,
  };
}
